import { useEffect, useState } from "react";
import { X, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import type { CinematicMoment } from "@/lib/cinematic/cinematicMomentsEngine";
import { useLanguage } from "@/hooks/useLanguage";
import { shareTargets } from "@/lib/referrals";
import { sponsorFor } from "@/lib/sponsor/sponsorMoments";
import { SponsorMomentBadge } from "@/components/sponsor/SponsorMomentBadge";
import { ExportClipButton } from "@/components/clip/ExportClipButton";
import { ViralPotentialBadge } from "@/components/viral/ViralPotentialBadge";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";

const ACCENT_GLOW: Record<CinematicMoment["accent"], string> = {
  rose: "oklch(0.7 0.22 20)",
  amber: "oklch(0.82 0.18 75)",
  cyan: "oklch(0.78 0.16 210)",
  violet: "oklch(0.7 0.22 295)",
  emerald: "oklch(0.75 0.18 150)",
};

function formatHeadline(t: (k: string) => string, moment: CinematicMoment) {
  let out = t(moment.headlineKey);
  if (moment.tokens) {
    for (const [k, v] of Object.entries(moment.tokens)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return out;
}
function formatSub(t: (k: string) => string, moment: CinematicMoment) {
  if (!moment.subKey) return "";
  let out = t(moment.subKey);
  if (moment.tokens) {
    for (const [k, v] of Object.entries(moment.tokens)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return out;
}

export function CinematicMomentCard({
  moment,
  onDismiss,
}: { moment: CinematicMoment; onDismiss?: () => void }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [enter, setEnter] = useState(false);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setEnter(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const headline = formatHeadline(t, moment);
  const sub = formatSub(t, moment);
  const glow = ACCENT_GLOW[moment.accent];
  const shareUrl = buildCanonicalShareUrl("/", { source: "cinematic_moment", medium: "share", campaign: moment.kind });
  const message = `${moment.emoji} ${headline} — VerseSport ${shareUrl}`;
  const targets = shareTargets(shareUrl, message);
  const sponsor = sponsorFor(moment.kind);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success(t("cm_copied"));
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error(t("cm_copy_failed"));
    }
  };

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share({
          title: "VerseSport",
          text: `${moment.emoji} ${headline}`,
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      void onCopy();
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative overflow-hidden rounded-2xl border border-border/60 bg-background/85 p-4 backdrop-blur transition-all duration-300 ${enter ? "translate-y-0 opacity-100 scale-100" : "translate-y-2 opacity-0 scale-95"}`}
      style={{
        boxShadow: `0 0 0 1px ${glow}33, 0 10px 36px -10px ${glow}80`,
      }}
    >
      {/* shockwave */}
      <span
        aria-hidden
        className="pointer-events-none absolute -inset-1 animate-pulse"
        style={{
          background: `radial-gradient(60% 60% at 20% 0%, ${glow}33 0%, transparent 70%)`,
          opacity: 0.6 + moment.intensity * 0.4,
        }}
      />
      <div className="relative flex items-start gap-3">
        <div className="text-3xl leading-none" aria-hidden>{moment.emoji}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black leading-tight">{headline}</div>
          {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] font-bold">
            <button
              onClick={nativeShare}
              className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-primary-foreground active:scale-95"
            >
              <Share2 className="h-3 w-3" /> {t("cm_share")}
            </button>
            <button
              onClick={onCopy}
              className="flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 active:scale-95"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? t("cm_copied_short") : t("cm_copy")}
            </button>
            <a href={targets.whatsapp} target="_blank" rel="noreferrer" className="rounded-full border border-border bg-background px-2.5 py-1">WhatsApp</a>
            <a href={targets.x} target="_blank" rel="noreferrer" className="rounded-full border border-border bg-background px-2.5 py-1">X</a>
            <ExportClipButton
              clip={{
                kind:
                  moment.kind === "rivalry_surge" || moment.kind === "nation_dominance"
                    ? "batalla_naciones"
                    : "mi_clip_viral",
                headline,
                subline: sub,
                accentHex: glow.includes("oklch") ? "#ef4444" : glow,
                cta: "Reta a tu nación →",
              }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <ViralPotentialBadge
              input={{
                kind: moment.kind,
                hypeIndex: Math.round(moment.intensity * 100),
                isRivalry: moment.kind === "rivalry_surge" || moment.kind === "nation_dominance",
                isComeback: moment.kind === "community_shockwave",
              }}
            />
            {sponsor && <SponsorMomentBadge sponsor={sponsor} />}
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} aria-label={t("common_close")} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}